/**
 * Ein Projekt reisefertig machen (PV-B01).
 *
 * Kartenkacheln, Gebaeudeumrisse und Gelaendedaten fuer den Umkreis holen,
 * damit vor Ort nichts mehr gebraucht wird. Das ist der einzige Schritt im
 * ganzen Programm, der Verbindung voraussetzt — deshalb steht er auch an zwei
 * Stellen: in der Projektliste vor der Abfahrt, und im Projekt selbst, gleich
 * unter der Adresse. Wer dort gerade den Standort gesetzt hat, will nicht erst
 * wieder hinausgehen.
 *
 * Beide Stellen rufen dieselbe Funktion. Zwei Fassungen desselben Ablaufs
 * laufen sonst auseinander, und die eine laedt eines Tages etwas, was die
 * andere weglaesst.
 */

import { anmeldung } from './abgleich.ts';
import { db } from './db.ts';
import { ladeGebaeude } from './gebaeude.ts';

/**
 * So weit werden Umrisse mindestens geholt, unabhaengig vom Kachelumkreis.
 * Der Server laesst hoechstens 600 zu.
 */
const UMRISSE_MINDESTUMKREIS_M = 400;
import { holeGelaendedaten } from './hoehendaten.ts';
import { kachelliste, ladeKacheln } from './kacheln.ts';

export interface Vorladeregeln {
  umkreisM: number;
  minZoom: number;
  maxZoom: number;
}

export interface Fortschritt {
  geladen: number;
  gesamt: number;
}

export interface Vorbereitungsbericht {
  kacheln: Fortschritt;
  /** Wie viele Gebaeudeumrisse liegen jetzt lokal, oder null bei einer Absage. */
  gebaeude: number | null;
  /** Der Grund, falls die Umrisse nicht kamen — im Klartext fuer die Anzeige. */
  gebaeudemeldung: string | null;
  /** Der Grund, falls es keine Gelaendedaten gibt. Ohne sie wird von Hand verschattet. */
  gelaendemeldung: string | null;
}

export async function bereiteProjektVor(
  projekt: { id: string; lat: number; lng: number },
  quelle: { url: string; maxZoom: number },
  vorladen: Vorladeregeln,
  onFortschritt?: (stand: Fortschritt) => void,
): Promise<Vorbereitungsbericht> {
  const kacheln = kachelliste(
    projekt.lat,
    projekt.lng,
    vorladen.umkreisM,
    vorladen.minZoom,
    Math.min(vorladen.maxZoom, quelle.maxZoom),
  );
  const stand = await ladeKacheln(quelle.url, kacheln, onFortschritt);
  await db.kachelstand.put({
    projekt_id: projekt.id,
    geladen: stand.geladen,
    gesamt: stand.gesamt,
    stand: new Date().toISOString(),
  });

  // Gebaeudeumrisse gehoeren dazu: Sie sind winzig und ohne sie gibt es vor Ort
  // keinen Flaechenvorschlag (PV-D18). Den Grund einer Absage weiterreichen —
  // ein ueberlasteter Dienst heisst "gleich nochmal", ein Fehler etwas anderes.
  //
  // Weiter als die Kacheln, und zwar absichtlich: Kacheln wachsen im Quadrat,
  // Umrisse fast nicht. Vierhundert Meter Kataster sind rund zweihundert
  // Kilobyte, dieselbe Flaeche als Luftbild ist ein Vielfaches davon. Vorher
  // reichte der Umriss genau bis zum Kachelrand — wer dann etwas weiter die
  // Strasse hinunter arbeitete, sah blankes Luftbild und dachte, der Vorschlag
  // sei kaputt.
  let gebaeude: number | null = null;
  let gebaeudemeldung: string | null = null;
  try {
    const umkreisUmrisseM = Math.max(vorladen.umkreisM, UMRISSE_MINDESTUMKREIS_M);
    gebaeude = await ladeGebaeude(projekt.id, projekt.lat, projekt.lng, umkreisUmrisseM);
  } catch (fehler) {
    gebaeudemeldung =
      fehler instanceof Error
        ? `${fehler.message} Ohne Umrisse müssen die Flächen von Hand gezeichnet werden.`
        : 'Gebäudeumrisse konnten nicht geladen werden — Flächen von Hand zeichnen.';
  }

  // Gelaendedaten nur, wenn der Server welche hat. Fehlen sie, bleibt die
  // Verschattung von Hand — mehr passiert nicht.
  let gelaendemeldung: string | null = null;
  try {
    const an = await anmeldung();
    const gelaende = await holeGelaendedaten(
      projekt.id,
      projekt.lat,
      projekt.lng,
      vorladen.umkreisM,
      an?.token ?? null,
    );
    if (!gelaende.ok) gelaendemeldung = gelaende.meldung;
  } catch {
    // Ohne Gelaendedaten laesst sich trotzdem planen.
  }

  return { kacheln: stand, gebaeude, gebaeudemeldung, gelaendemeldung };
}

/** Der Bericht in einem Satz, wie ihn beide Ansichten anzeigen. */
export function alsSatz(bericht: Vorbereitungsbericht): string {
  const teile = [
    `Karte ${bericht.kacheln.geladen} von ${bericht.kacheln.gesamt} Kacheln`,
    bericht.gebaeude !== null ? `${bericht.gebaeude} Gebäudeumriss(e)` : bericht.gebaeudemeldung,
    bericht.gelaendemeldung,
  ].filter(Boolean);
  return teile.join(' · ');
}
